import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Check } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", password_confirm: "", accept_cgv: false });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resending, setResending] = useState(false);

  const f = (field) => (e) => setForm({ ...form, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (form.password.length < 8) return toast.error("Le mot de passe doit contenir au moins 8 caractères");
    if (!form.accept_cgv) return toast.error("Veuillez accepter les conditions générales de vente");
    setLoading(true);
    try {
      await register(form);
      setPendingEmail(form.email);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(" ") : "Inscription échouée";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: pendingEmail });
      toast.success("E-mail renvoyé !");
    } catch {
      toast.error("Erreur lors du renvoi");
    } finally {
      setResending(false);
    }
  };

  if (pendingEmail) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="verification-pending">
        <div className="w-20 h-20 mx-auto rounded-full bg-terracotta/10 flex items-center justify-center mb-6">
          <Mail size={36} className="text-terracotta" />
        </div>
        <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">Presque fini !</p>
        <h1 className="font-serif-display text-3xl mb-4">Vérifiez votre e-mail</h1>
        <p className="text-[#6B655C] mb-2">Un e-mail de confirmation a été envoyé à :</p>
        <p className="font-medium text-[#1F1C18] mb-6" data-testid="pending-email">{pendingEmail}</p>
        <p className="text-sm text-[#6B655C] mb-8">
          Cliquez sur le lien dans l'e-mail pour activer votre compte. Le lien est valable 24 heures.
        </p>
        <button onClick={resendEmail} disabled={resending} className="text-sm text-terracotta hover:underline disabled:opacity-50" data-testid="resend-btn">
          {resending ? "Envoi en cours..." : "Renvoyer l'e-mail de confirmation"}
        </button>
        <p className="text-sm text-[#6B655C] mt-6">
          <Link to="/login" className="text-terracotta hover:underline">Retour à la connexion</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2 text-center">Bienvenue</p>
      <h1 className="font-serif-display text-4xl mb-10 text-center">Créer un compte</h1>
      <form onSubmit={submit} className="bg-white/70 backdrop-blur-md rounded-3xl border border-[#E5E1D8] p-8 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="reg-first" className="block text-sm text-[#6B655C] mb-1.5">Prénom</label>
            <input id="reg-first" required value={form.first_name} onChange={f("first_name")} placeholder="Prénom" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="register-first-name" />
          </div>
          <div>
            <label htmlFor="reg-last" className="block text-sm text-[#6B655C] mb-1.5">Nom</label>
            <input id="reg-last" required value={form.last_name} onChange={f("last_name")} placeholder="Nom" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="register-last-name" />
          </div>
        </div>
        <div>
          <label htmlFor="reg-email" className="block text-sm text-[#6B655C] mb-1.5">Adresse e-mail</label>
          <input id="reg-email" required type="email" value={form.email} onChange={f("email")} placeholder="votre@email.com" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="register-email" />
        </div>
        <div>
          <label htmlFor="reg-pw" className="block text-sm text-[#6B655C] mb-1.5">Mot de passe</label>
          <div className="relative">
            <input id="reg-pw" required type={showPw ? "text" : "password"} minLength={8} value={form.password} onChange={f("password")} placeholder="8 caractères minimum" className="w-full p-3 pr-11 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="register-password" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B655C] hover:text-terracotta">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="reg-pw2" className="block text-sm text-[#6B655C] mb-1.5">Confirmer le mot de passe</label>
          <input id="reg-pw2" required type={showPw ? "text" : "password"} minLength={8} value={form.password_confirm} onChange={f("password_confirm")} placeholder="Retapez votre mot de passe" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="register-password-confirm" />
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="cgv-register-label">
          <input type="checkbox" checked={form.accept_cgv} onChange={f("accept_cgv")} className="mt-0.5 w-4 h-4 rounded border-[#E5E1D8] accent-terracotta cursor-pointer shrink-0" data-testid="register-cgv-checkbox" />
          <span className="text-xs text-[#6B655C] leading-relaxed">
            J'accepte les{" "}
            <Link to="/cgv" target="_blank" className="text-terracotta underline">Conditions Générales de Vente</Link>
            {" "}et la{" "}
            <Link to="/cookies" target="_blank" className="text-terracotta underline">Politique de confidentialité</Link>
          </span>
        </label>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="register-submit-btn">
          {loading ? "Création..." : "Créer mon compte"}
        </button>
      </form>
      <p className="text-center text-sm text-[#6B655C] mt-6">
        Déjà inscrit(e) ?{" "}
        <Link to="/login" className="text-terracotta hover:underline" data-testid="goto-login">Se connecter</Link>
      </p>
    </div>
  );
}
